import { WAITLIST_TIER_RANK } from '@lume/shared';

import type { SchedulingSlotWire, WaitlistEntryWire, WaitlistPriorityTierWire } from '../../lib/api-client';

/**
 * waitlist-adapter — lógica PURA do painel de fila/capacidade (Frente B Bloco IV PR6).
 *
 * Sem React, sem IO — testável exaustivamente. Cobre:
 *   - rótulo PT + rank de cada `priorityTier` (mesma régua do motor de match do engine);
 *   - ocupação por slot de GRUPO derivada do `remainingCapacity` da disponibilidade;
 *   - quais ações o operador pode tomar sobre uma entry dado o seu `status`.
 */

/**
 * Rank canônico (menor = maior prioridade) — importado de `@lume/shared` p/ eliminar drift
 * UI↔motor: a UI ordena pela MESMA tabela que o engine usa em `matchWaitlistEntries` e
 * `WaitlistService.listWaitlist`. `WaitlistPriorityTierWire` tem os mesmos valores literais
 * que `WaitlistPriorityTier` do shared (paridade verificada — NIT-1 PR6).
 */
export const TIER_RANK: Record<WaitlistPriorityTierWire, number> = WAITLIST_TIER_RANK;

/** Rótulo PT curto de cada tier (UI do operador). */
export function tierLabel(tier: WaitlistPriorityTierWire): string {
  switch (tier) {
    case 'bumped':
      return 'Remanejado';
    case 'urgent_high':
      return 'Urgente (alta)';
    case 'urgent_medium':
      return 'Urgente (média)';
    case 'urgent_low':
      return 'Urgente (baixa)';
    case 'preferred':
      return 'Preferencial';
    case 'normal':
      return 'Normal';
  }
}

/** Rótulo PT do status da entry. */
export function statusLabel(status: WaitlistEntryWire['status']): string {
  switch (status) {
    case 'pending_approval':
      return 'Aguardando liberação';
    case 'active':
      return 'Na fila';
    case 'offered':
      return 'Vaga ofertada';
    case 'booked':
      return 'Agendado';
    case 'expired':
      return 'Expirado';
    case 'cancelled':
      return 'Cancelado';
  }
}

/**
 * Ordena a fila pela MESMA régua da promoção do engine: `(rank do tier, createdAt FIFO)`.
 * O backend já retorna ordenado; reordenar no cliente mantém estável após mutações otimistas.
 */
export function sortQueue(entries: WaitlistEntryWire[]): WaitlistEntryWire[] {
  return [...entries].sort((a, b) => {
    const rankDiff = TIER_RANK[a.priorityTier] - TIER_RANK[b.priorityTier];
    if (rankDiff !== 0) return rankDiff;
    return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
  });
}

/** Ações que o operador pode tomar sobre a entry, dado o status atual. */
export interface EntryActions {
  canApprove: boolean; // pending_approval → active
  canReject: boolean; // pending_approval → cancelled
  canReprioritize: boolean; // fila viva
  canPromote: boolean; // liberar manual (só active)
}

export function actionsFor(status: WaitlistEntryWire['status']): EntryActions {
  const isPending = status === 'pending_approval';
  const isLive = status === 'pending_approval' || status === 'active' || status === 'offered';
  return {
    canApprove: isPending,
    canReject: isPending,
    canReprioritize: isLive,
    canPromote: status === 'active',
  };
}

/** Capacidade/ocupação de um slot de grupo derivada da disponibilidade. */
export interface SlotOccupancy {
  startsAt: string;
  endsAt: string;
  capacity: number; // total de vagas do slot
  remaining: number; // vagas livres
  occupied: number; // vagas tomadas
  resourceIds: string[];
}

/**
 * Deriva ocupação por slot a partir dos slots de disponibilidade + a capacidade do recurso.
 * `remainingCapacity` já vem do free/busy; a capacidade total vem do recurso que atende o slot
 * (passada via `capacityByResource`). Sem o recurso no mapa, assume `capacity = remaining`
 * (slot exclusivo / capacidade desconhecida → ocupação 0, honesto).
 */
export function deriveOccupancy(
  slots: SchedulingSlotWire[],
  capacityByResource: Map<string, number>,
): SlotOccupancy[] {
  return slots.map((s) => {
    // A capacidade do slot é a MAIOR capacidade entre os recursos que o satisfazem
    // (grupo fungível: qualquer um dos recursos do tipo cobre o slot).
    const capacity = s.resourceIds.reduce(
      (max, id) => Math.max(max, capacityByResource.get(id) ?? 0),
      0,
    );
    const effCapacity = capacity > 0 ? capacity : s.remainingCapacity;
    const remaining = Math.min(s.remainingCapacity, effCapacity);
    return {
      startsAt: s.startsAt,
      endsAt: s.endsAt,
      capacity: effCapacity,
      remaining,
      occupied: Math.max(0, effCapacity - remaining),
      resourceIds: s.resourceIds,
    };
  });
}

/** Há ao menos um slot de GRUPO (capacity > 1) na disponibilidade? (controla a seção de capacidade) */
export function hasGroupSlots(occupancy: SlotOccupancy[]): boolean {
  return occupancy.some((o) => o.capacity > 1);
}
